/**
 * The two sides of the app and who belongs on which.
 *
 * - `staff` / `admin` — BOI staff, signed in with Google SSO, on the BOI cockpit.
 * - `client` — client users, signed in with email + password, on the client portal.
 *
 * These helpers only shape navigation; the backend re-checks the role from the
 * database on every privileged request.
 */

import type { UserProfile } from "@/lib/api";

export type Role = UserProfile["role"];

export const STAFF_HOME = "/cockpit";
export const CLIENT_HOME = "/portal";

/** True for BOI staff (including admins). */
export function isStaffRole(role: Role | null | undefined): boolean {
  return role === "staff" || role === "admin";
}

/** Where a signed-in user of this role lands. */
export function homeForRole(role: Role): string {
  return isStaffRole(role) ? STAFF_HOME : CLIENT_HOME;
}

/** Human label for a role. */
export function roleLabel(role: Role): string {
  switch (role) {
    case "admin":
      return "Admin";
    case "staff":
      return "Staff";
    case "client":
      return "Client";
  }
}

// ─── Return-to after sign-in (`?next=`) ───────────────────────────────────
//
// These mirror the backend's `auth/redirects.py`, which checks the same value
// again before the Google flow follows it. Keep the two in step: a rule that
// only one side applies is an open redirect on the other.

/** The longest `next` value either side will follow. */
export const MAX_NEXT_LENGTH = 512;

/** Most decoding passes a `next` may need before it stops changing. */
const MAX_DECODE_PASSES = 3;

const PRINTABLE_ASCII = /^[\x21-\x7e]+$/;
const MALFORMED_PERCENT = /%(?![0-9A-Fa-f]{2})/;

/** The path prefixes of each role's side of the app. */
const SIDE_PREFIXES: Record<Role, readonly string[]> = {
  staff: [STAFF_HOME],
  admin: [STAFF_HOME, "/admin"],
  client: [CLIENT_HOME],
};

/**
 * True when `raw` is a same-origin path that is safe to navigate to: printable
 * ASCII up to {@link MAX_NEXT_LENGTH} characters, well-formed `%XX` escapes, and
 * every decoding of it (up to three passes) a single-slash path with no `//`,
 * backslash, `#`, control character or `.`/`..` segment.
 */
export function isSafeRelativePath(raw: string | null | undefined): boolean {
  return !!raw && decodedSafePath(raw) !== null;
}

/**
 * `raw` when it is safe and points into `role`'s own side of the app (the
 * cockpit for staff, plus `/admin` for admins, the portal for clients);
 * otherwise `null`, and the caller falls back to the role's home.
 */
export function safeNextForRole(raw: string | null | undefined, role: Role): string | null {
  if (!raw) {
    return null;
  }
  const path = decodedSafePath(raw);
  if (path === null) {
    return null;
  }
  const onOwnSide = SIDE_PREFIXES[role].some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  return onOwnSide ? raw : null;
}

/** The fully decoded path portion of a safe `raw`, or `null` when it is not safe. */
function decodedSafePath(raw: string): string | null {
  if (raw.length > MAX_NEXT_LENGTH || !PRINTABLE_ASCII.test(raw) || MALFORMED_PERCENT.test(raw)) {
    return null;
  }
  const forms = [raw];
  let current = raw;
  for (let pass = 0; pass < MAX_DECODE_PASSES; pass++) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      // Invalid UTF-8, or a `%` that decoding exposed without two hex digits.
      return null;
    }
    if (decoded === current) {
      return forms.every(isSafeForm) ? pathPortion(current) : null;
    }
    forms.push(decoded);
    current = decoded;
  }
  // Still changing after the last pass: more encoding layers than any real link has.
  return null;
}

function isSafeForm(form: string): boolean {
  if (
    !form.startsWith("/") ||
    form.includes("//") ||
    form.includes("\\") ||
    form.includes("#") ||
    hasControlCharacter(form)
  ) {
    return false;
  }
  return !pathPortion(form)
    .split("/")
    .some((segment) => segment === "." || segment === "..");
}

/** C0 controls, DEL and C1 controls. */
function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) {
      return true;
    }
  }
  return false;
}

function pathPortion(form: string): string {
  const query = form.indexOf("?");
  return query === -1 ? form : form.slice(0, query);
}
